import React from 'react';
import { Box, Typography } from '@mui/material';
import Reaptcha from 'reaptcha';
import { appConfig } from 'config/appConfig';

export interface RecaptchaChallengeProps {
  open: boolean;
  onTokenChange: (token: string) => void;
  promptIncomplete?: boolean;
  rejected?: boolean;
}

export const RecaptchaChallenge: React.FC<RecaptchaChallengeProps> = ({
  open,
  onTokenChange,
  promptIncomplete = false,
  rejected = false,
}) => {
  const siteKey = appConfig.siteKey ?? '';
  const [captchaError, setCaptchaError] = React.useState(false);
  const captchaRef = React.useRef<Reaptcha | null>(null);
  // explicit render fires onLoad once. Without this guard a later render would call renderExplicitly again.
  const renderRequestedRef = React.useRef(false);
  // rejected stays true while the error is on screen. Remember that this failure already cleared the checkbox.
  const rejectedResetRef = React.useRef(false);
  // Kept in a ref so a new callback identity does not re-run the reset effect.
  const onTokenChangeRef = React.useRef(onTokenChange);
  onTokenChangeRef.current = onTokenChange;

  const resetChallenge = React.useCallback(() => {
    setCaptchaError(false);
    captchaRef.current?.reset().catch(() => undefined);
    onTokenChangeRef.current('');
  }, []);

  React.useEffect(() => {
    if (!open) {
      renderRequestedRef.current = false;
      rejectedResetRef.current = false;
      resetChallenge();
      return;
    }

    // A new request clears the error, so rejected becomes false. Allow the next failure to reset,
    // but leave the solved checkbox in place while that request is running.
    if (!rejected) {
      rejectedResetRef.current = false;
      return;
    }

    if (rejectedResetRef.current) return;
    rejectedResetRef.current = true;
    // The token was already sent. Clear the checkbox once so the next attempt needs a new one.
    resetChallenge();
  }, [open, rejected, resetChallenge]);

  const hasSiteKey = Boolean(siteKey);

  if (!hasSiteKey) {
    return (
      <Typography color="error" variant="body2">
        reCAPTCHA is unavailable right now. Please try again later.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: 1,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Reaptcha
          ref={captchaRef}
          sitekey={siteKey}
          size="normal"
          explicit
          onLoad={() => {
            if (renderRequestedRef.current) return;
            renderRequestedRef.current = true;
            captchaRef.current?.renderExplicitly().catch(() => undefined);
          }}
          onVerify={(token: string) => {
            onTokenChangeRef.current(token);
            setCaptchaError(false);
          }}
          onExpire={() => {
            onTokenChangeRef.current('');
            setCaptchaError(true);
          }}
        />
      </Box>
      {(captchaError || promptIncomplete) && (
        <Typography color="error" variant="caption">
          Please complete the reCAPTCHA to continue.
        </Typography>
      )}
    </Box>
  );
};
